/**
 * PROC-07 Paragraph-closure uniformity (INV-PACK-07, ENGINE-SPEC §4; change C-AMB09-PARA).
 *
 * Proportion of substantial paragraphs (more than 15 words) whose last sentence has at most 9
 * words: reward-tuned generation lands every unit on a short epigram. The pack split paragraphs on
 * every single newline (AMB-09, so hard-wrapped text fragmented); here paragraphs are the document
 * model's own (blank-line blocks, prose register). Exposure = substantial paragraphs, event = a
 * short closer, so the estimate is a proportion with a binomial interval.
 *
 * Weight 1.5 × the pack weight 0.45; one-way, nd 0.15; ramp = the pack thresholds
 * `[[0,0],[0.12,0],[0.22,100]]` (human ceiling 0.12, machine floor 0.22).
 */
import { derivePrior, makeEvidence, rateChannel } from './framework.ts';
import { proseParagraphs } from './helpers.ts';
import type { ChannelDef, Measurement, Ramp, RunContext } from './types.ts';

const RAMP: Ramp = [
	[0, 0],
	[0.12, 0],
	[0.22, 100],
];

const SUBSTANTIAL_WORDS = 15;
const SHORT_CLOSER_WORDS = 9;

export const proc07Def: ChannelDef = {
	id: 'PROC-07',
	name: 'Paragraph-closure uniformity',
	family: 'RHYTHM',
	axis: 'authorship',
	registers: ['prose'],
	direction: 1,
	oneWay: true,
	estimator: 'proportion',
	features: [{ id: 'shortCloser', label: 'Paragraphs ending on a sentence of 9 words or fewer' }],
	minExposure: 60,
	exposureUnit: 'paragraphs',
	lang: 'agnostic',
	ramp: RAMP,
	unitScale: 1,
	unitLabel: 'share of substantial paragraphs',
	prior: derivePrior(RAMP, 1, 'paragraphs', 1.5),
	weight: 1.5 * 0.45,
	nd: 0.15,
	changes: ['C-AMB09-PARA'],
};

function measure(ctx: RunContext): Measurement {
	const { doc } = ctx;
	const pp = doc.paragraphs;
	const S = doc.sentences;
	const P = pp.count;
	const k = new Float64Array(P);
	const e = new Float64Array(P);
	const ev = makeEvidence();
	const examples: string[] = [];
	let hits = 0;
	let total = 0;
	for (const p of proseParagraphs(doc)) {
		if ((pp.ownWords[p] as number) <= SUBSTANTIAL_WORDS) continue;
		const block = pp.block[p] as number;
		// last sentence of the own block that carries a word (the seed required a letter)
		let last = -1;
		for (let s = pp.ownSentStart[p] as number; s < (pp.sentEnd[p] as number); s++) {
			if ((S.block[s] as number) !== block) {
				if (last >= 0) break;
				continue;
			}
			if ((S.words[s] as number) > 0) last = s;
		}
		if (last < 0) continue;
		e[p] = 1;
		total++;
		const w = S.words[last] as number;
		if (w > 0 && w <= SHORT_CLOSER_WORDS) {
			k[p] = 1;
			hits++;
			const a = S.charStart[last] as number;
			const en = S.charEnd[last] as number;
			ev.add(a, en, 0, 1);
			if (examples.length < 5) examples.push(doc.text.slice(a, en));
		}
	}
	return {
		k,
		e,
		evidence: ev,
		summary: [`${hits}/${total} paragraphs end <=9 words`, ...examples],
	};
}

export const proc07 = rateChannel(proc07Def, measure);
export { proc07Def as def, proc07 as impl };
